/**
 * MOSAIC Sealed USB Edition — teacher-facing shell (preview)
 *
 * Rendered when someone visits /usb-edition/*
 *
 * Renders WITHOUT the marketing chrome (no top nav, no footer, no banners).
 * Trimmed nav: My Classes · Scoring · Reports · Item Bank · Help · Support
 * Top-right: Sealed Edition serial badge + Offline pill.
 * First-visit: EULA modal must be accepted before the app is usable.
 *
 * This preview build simulates what the USB will render locally — same
 * React code, same design tokens, no network calls to /api/*.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/* ─── EULA content (also written to /public/usb/EULA.txt for the physical USB) ── */
const EULA_TEXT = `MOSAIC SEALED USB EDITION — END USER LICENSE AGREEMENT
Version 1.0 · Effective 2026-09-25

This USB drive contains MOSAIC Assessment Co. proprietary content that is
licensed — not sold — to your school building. By clicking Accept you agree:

1. Use is limited to educators employed by the licensed school building.
2. Content is protected by U.S. copyright. No copying, redistribution,
   or extraction of items, rubrics, or answer keys is permitted.
3. No student data on this drive will be transmitted to any network,
   cloud service, or third party. All rubric scores are stored locally
   on the drive itself.
4. This USB is serialized. Loss or unauthorized transfer must be reported
   to MOSAIC Assessment Co. within 48 hours at croca.edu@gmail.com.
5. This license expires at the end of the current school year. The drive
   remains usable in read-only mode for archival access after expiry.

If you do not accept these terms, remove the USB and return it to your
school coordinator.`;

/* ─── EULA modal (localStorage acceptance) ────────────────────── */
function EulaModal({ onAccept }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      data-testid="usb-eula-modal"
    >
      <div className="w-full max-w-2xl rounded-lg bg-white p-8 shadow-2xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="h-2 w-8 rounded-full bg-[#B23A48]" />
          <p className="text-xs font-bold uppercase tracking-widest text-[#B23A48]">
            First-launch agreement
          </p>
        </div>
        <h2 className="mb-4 text-2xl font-bold text-[#0F1B2D]">
          Sealed MOSAIC USB Edition
        </h2>
        <pre className="mb-6 max-h-64 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
          {EULA_TEXT}
        </pre>
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => (window.location.href = "about:blank")}
            data-testid="usb-eula-decline-btn"
          >
            Decline &amp; Eject
          </Button>
          <Button
            onClick={onAccept}
            className="bg-[#0F1B2D] hover:bg-[#1a2a44]"
            data-testid="usb-eula-accept-btn"
          >
            I Accept — Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Nav (trimmed teacher-only surface) ──────────────────────── */
const NAV_ITEMS = [
  { to: "/usb-edition", label: "My Classes", end: true, testid: "usb-nav-classes" },
  { to: "/usb-edition/scoring", label: "Scoring", testid: "usb-nav-scoring" },
  { to: "/usb-edition/reports", label: "Reports", testid: "usb-nav-reports" },
  { to: "/usb-edition/item-bank", label: "Item Bank", testid: "usb-nav-item-bank" },
  { to: "/usb-edition/help", label: "Help", testid: "usb-nav-help" },
  { to: "/usb-edition/support", label: "Support", testid: "usb-nav-support" },
];

function TopBar({ serial }) {
  const { pathname } = useLocation();
  const isActive = (item) =>
    item.end ? pathname === item.to : pathname.startsWith(item.to);

  return (
    <header
      className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur"
      data-testid="usb-topbar"
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-6">
        <div className="flex items-center gap-8">
          <Link
            to="/usb-edition"
            className="text-lg font-bold tracking-tight text-[#0F1B2D]"
            data-testid="usb-brand"
          >
            MOSAIC
          </Link>
          <nav className="hidden gap-1 md:flex">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`rounded px-3 py-1.5 text-sm transition-colors ${
                  isActive(item)
                    ? "bg-[#0F1B2D] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2" data-testid="usb-serial-badge">
          <Badge variant="outline" className="border-slate-300 font-mono text-xs">
            Serial #{serial}
          </Badge>
          <Badge className="bg-emerald-600 hover:bg-emerald-600 text-xs">
            Offline
          </Badge>
        </div>
      </div>
    </header>
  );
}

/* ─── Screens (Week-1 shells; deeper functionality in Week 2/3) ── */
function MyClasses() {
  const classes = [
    { grade: "6", period: "P1", students: 24 },
    { grade: "7", period: "P3", students: 22 },
    { grade: "8", period: "P5", students: 21 },
  ];
  return (
    <section className="mx-auto max-w-7xl px-6 py-10">
      <p className="text-xs font-bold uppercase tracking-widest text-[#B23A48]">
        Welcome
      </p>
      <h1 className="mt-1 text-3xl font-bold text-[#0F1B2D]" data-testid="usb-classes-title">
        Choose a class to begin scoring
      </h1>
      <p className="mt-2 text-sm text-slate-600">
        All rubric scores you enter are saved directly to this USB drive. Nothing
        transmits to any network.
      </p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {classes.map((c) => (
          <Card
            key={`${c.grade}-${c.period}`}
            className="cursor-pointer border-slate-200 transition-shadow hover:shadow-lg"
            data-testid={`usb-class-card-g${c.grade}-${c.period}`}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#0F1B2D]">
                Grade {c.grade} · {c.period}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500">{c.students} students</p>
              <Button
                className="mt-4 w-full bg-[#0F1B2D] hover:bg-[#1a2a44]"
                data-testid={`usb-class-open-g${c.grade}-${c.period}`}
              >
                Open
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

function Scoring() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-10" data-testid="usb-scoring">
      <p className="text-xs font-bold uppercase tracking-widest text-[#B23A48]">
        Rubric scoring
      </p>
      <h1 className="mt-1 text-3xl font-bold text-[#0F1B2D]">Scoring</h1>
      <Card className="mt-6 border-slate-200">
        <CardContent className="pt-6">
          <p className="text-slate-600">
            The rubric scoring interface will render here in Week 2, using the
            same components and design tokens as the cloud version.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}

function Reports() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-10" data-testid="usb-reports">
      <p className="text-xs font-bold uppercase tracking-widest text-[#B23A48]">
        Class reports
      </p>
      <h1 className="mt-1 text-3xl font-bold text-[#0F1B2D]">Reports</h1>
      <Card className="mt-6 border-slate-200">
        <CardContent className="pt-6">
          <p className="text-slate-600">
            Single-class WIDA-level growth reports will render here — same visual
            grammar as cloud Reports, minus cross-district roll-ups.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}

function ItemBank() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-10" data-testid="usb-item-bank">
      <p className="text-xs font-bold uppercase tracking-widest text-[#B23A48]">
        Preview
      </p>
      <h1 className="mt-1 text-3xl font-bold text-[#0F1B2D]">Item Bank</h1>
      <Card className="mt-6 border-slate-200">
        <CardContent className="pt-6">
          <p className="text-slate-600">
            Browse every hand-crafted item in your licensed grade band before class.
            All content is bundled on this USB — nothing streams.
          </p>
        </CardContent>
      </Card>
    </section>
  );
}

function Help() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-10" data-testid="usb-help">
      <p className="text-xs font-bold uppercase tracking-widest text-[#B23A48]">
        How-to
      </p>
      <h1 className="mt-1 text-3xl font-bold text-[#0F1B2D]">Help &amp; How-To</h1>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {[
          "Getting started (5 min)",
          "How to score a rubric card",
          "How to enter scores efficiently",
          "How to print a class report",
          "Frequently asked questions",
          "End-of-year handoff checklist",
        ].map((t) => (
          <Card
            key={t}
            className="cursor-pointer border-slate-200 hover:shadow-md"
            data-testid={`usb-help-card-${t.replace(/\W+/g, "-").toLowerCase()}`}
          >
            <CardContent className="pt-6">
              <p className="font-medium text-[#0F1B2D]">{t}</p>
              <p className="mt-1 text-xs text-slate-500">Static PDF · bundled on USB</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

function Support() {
  return (
    <section className="mx-auto max-w-3xl px-6 py-10" data-testid="usb-support">
      <p className="text-xs font-bold uppercase tracking-widest text-[#B23A48]">
        Need help?
      </p>
      <h1 className="mt-1 text-3xl font-bold text-[#0F1B2D]">Contact MOSAIC</h1>
      <Card className="mt-6 border-slate-200 bg-slate-50">
        <CardContent className="space-y-4 pt-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Email
            </p>
            <p className="mt-1 text-lg text-[#0F1B2D]">croca.edu@gmail.com</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Phone
            </p>
            <p className="mt-1 text-lg text-[#0F1B2D]">973-303-3414</p>
          </div>
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">Support tip</p>
            <p className="mt-1">
              Screenshot this screen if you call us — the serial number in the
              top-right corner lets us pull up your building instantly.
            </p>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

/* ─── Root shell ──────────────────────────────────────────────── */
export default function UsbEdition() {
  const [accepted, setAccepted] = useState(
    () => localStorage.getItem("mosaic_usb_eula_accepted") === "1"
  );

  // In the real USB build the serial is baked into the launcher config.
  // In the preview we take it from ?serial= or fall back to a demo value.
  const serial = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("serial") ||
      localStorage.getItem("mosaic_usb_demo_serial") ||
      "PREVIEW"
    );
  }, []);

  useEffect(() => {
    // Kill any accidentally-injected analytics / tracker globals on this shell.
    // Belt-and-braces for the "no telemetry" promise.
    ["ga", "gtag", "dataLayer", "fbq", "mixpanel", "amplitude"].forEach((k) => {
      try {
        delete window[k];
      } catch (_) {}
    });
  }, []);

  const acceptEula = () => {
    localStorage.setItem("mosaic_usb_eula_accepted", "1");
    localStorage.setItem("mosaic_usb_eula_accepted_at", new Date().toISOString());
    setAccepted(true);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="usb-edition-shell">
      {!accepted && <EulaModal onAccept={acceptEula} />}
      <TopBar serial={serial} />
      <main className={accepted ? "" : "pointer-events-none select-none blur-sm"}>
        <Routes>
          <Route index element={<MyClasses />} />
          <Route path="scoring" element={<Scoring />} />
          <Route path="reports" element={<Reports />} />
          <Route path="item-bank" element={<ItemBank />} />
          <Route path="help" element={<Help />} />
          <Route path="support" element={<Support />} />
        </Routes>
      </main>
      <footer className="border-t border-slate-200 bg-slate-50 py-6">
        <div className="mx-auto max-w-7xl px-6 text-xs text-slate-500">
          MOSAIC Sealed USB Edition · Offline · District- or School-Owned · Serial #{serial}
        </div>
      </footer>
    </div>
  );
}
